import { useState, useEffect, useCallback, RefObject } from 'react'

interface FullscreenAPI {
    requestFullscreen?: () => Promise<void>
    webkitRequestFullscreen?: () => Promise<void>
    mozRequestFullScreen?: () => Promise<void>
    msRequestFullscreen?: () => Promise<void>
}

interface DocumentFullscreenAPI {
    exitFullscreen?: () => Promise<void>
    webkitExitFullscreen?: () => Promise<void>
    mozCancelFullScreen?: () => Promise<void>
    msExitFullscreen?: () => Promise<void>
    fullscreenElement?: Element
    webkitFullscreenElement?: Element
    mozFullScreenElement?: Element
    msFullscreenElement?: Element
}

interface UseFullscreenReturn {
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
    enterFullscreen: () => Promise<void>
    exitFullscreen: () => Promise<void>
}

const useFullscreen = (containerRef: RefObject<HTMLElement>): UseFullscreenReturn => {
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false)

    const handleFullscreenChange = useCallback((): void => {
        const doc = document as DocumentFullscreenAPI
        setIsFullscreen(
            Boolean(
                doc.fullscreenElement ||
                    doc.webkitFullscreenElement ||
                    doc.mozFullScreenElement ||
                    doc.msFullscreenElement
            )
        )
    }, [])

    const enterFullscreen = useCallback(async (): Promise<void> => {
        if (!containerRef.current) return

        const element = containerRef.current as HTMLElement & FullscreenAPI

        try {
            await (element.requestFullscreen?.() ||
                element.webkitRequestFullscreen?.() ||
                element.mozRequestFullScreen?.() ||
                element.msRequestFullscreen?.())
        } catch (error) {
            console.warn('Enter fullscreen operation failed:', error)
        }
    }, [containerRef])

    const exitFullscreen = useCallback(async (): Promise<void> => {
        const doc = document as DocumentFullscreenAPI

        try {
            await (doc.exitFullscreen?.() ||
                doc.webkitExitFullscreen?.() ||
                doc.mozCancelFullScreen?.() ||
                doc.msExitFullscreen?.())
        } catch (error) {
            console.warn('Exit fullscreen operation failed:', error)
        }
    }, [])

    const toggleFullscreen = useCallback(async (): Promise<void> => {
        if (isFullscreen) {
            await exitFullscreen()
        } else {
            await enterFullscreen()
        }
    }, [isFullscreen, enterFullscreen, exitFullscreen])

    useEffect(() => {
        const events = [
            'fullscreenchange',
            'webkitfullscreenchange',
            'mozfullscreenchange',
            'MSFullscreenChange',
        ] as const

        events.forEach((event) => {
            document.addEventListener(event, handleFullscreenChange)
        })

        return () => {
            events.forEach((event) => {
                document.removeEventListener(event, handleFullscreenChange)
            })
        }
    }, [handleFullscreenChange])

    return {
        isFullscreen,
        toggleFullscreen,
        enterFullscreen,
        exitFullscreen,
    }
}

export default useFullscreen
